import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from "@material-ui/core";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import { useState } from "react";
import { Pagination } from "../../../components/Pagination";
import { PerPage } from "../../../components/PerPage";
import { TableConfig } from "../../../config/Config";

import styles from "../Settings.module.scss";

const BulkTable = ({ tableHeaderObj, data, bulkUpdateFunction, title, selected, setSelected, sort = null, setSort = null, pagination = null, setPagination = null }) => {
  const [open, setOpen] = useState(false)

  const offset = pagination?.page ?? 1;
  const perPage = pagination ? pagination.pageSize : TableConfig.resultPerPage
  const totalData = pagination ? pagination.total : data.length;

  const handleDialog = () => {
    setOpen(true)
  }

  const handleBulkUpdate = async () => {
    if (selected.length === 0) return;
    const result = await bulkUpdateFunction(selected);
    if (result) {
      setSelected([]);
    }
  }

  const isSelected = (id) => {
    return selected.some(item => {
      const itemIdKey = Object.keys(item)[0]; // get the key in selected item
      return item[itemIdKey] === id;
    });
  };

  const handleClose = () => {
    setOpen(false)
  }

  const handlePaginationChange = offset => {
    const page = offset.selected + 1;
    if (setPagination) {
      setPagination(prev => ({ ...prev, page: page }))
    }
  };

  const handleSortClick = (column) => {
    if (!column.sortable) return;

    setSort(prev => ({
      columnName: column.name,
      order:
        prev?.columnName === column.name && prev?.order === "asc" ? "desc" : "asc"
    }));

    // reset to first page whenever sort changes
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleStatusToggle = (row, col, checked) => {
    const idKey = Object.keys(row)[0];
    const rowId = row[idKey];

    setSelected(prev => {
      const index = prev.findIndex(item => item[idKey] === rowId);
      if (index !== -1) {
        const newArr = [...prev];
        newArr[index] = { ...newArr[index], [col]: checked };
        return newArr;
      } else {
        // store display value too
        return [...prev, { [idKey]: rowId, display: row.title, [col]: checked }];
      }
    });
  };

  return (
    <>
      <div className="d-flex justify-content-end mb-3">
        <Button
          style={{
            backgroundColor: "#e35646",
            color: "white",
          }}
          onClick={() => handleDialog(true)}
          disabled={selected.length == 0}
        >Save</Button>
      </div>
      <TableContainer component={Paper} elevation={0}>
        <Table aria-label="Team" className={styles.table}>
          <TableHead className={styles.tableHead}>
            <TableRow>
              {tableHeaderObj.map((tableHead, index) => {
                const isActiveSort = sort && sort.columnName === tableHead.name;
                const sortOrder = isActiveSort ? sort.order : null;
                return (
                  <TableCell
                    key={index}
                    className={styles.tableHeadCell}
                    onClick={() => handleSortClick(tableHead)}
                  >
                    {tableHead.label}
                    {tableHead.sortable && (
                      <span>
                        {sortOrder === "asc" ?
                          <KeyboardArrowUpIcon fontSize="small" /> :
                          <KeyboardArrowDownIcon fontSize="small" />
                        }
                      </span>)}
                  </TableCell>
                )
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, index) => {
              const id = Object.keys(row)[0]
              const isItemSelected = isSelected(row[id]);
              return (
                <TableRow
                  key={index}
                  className={styles.tableRow}
                  selected={isItemSelected}
                  hover
                >
                  {tableHeaderObj.map((col, index) => {
                    const idKey = Object.keys(row)[0];
                    const rowId = row[idKey];
                    const selectedRow = selected.find(item => item[idKey] === rowId);

                    // Use updated value if exists in selected, else fallback to row
                    const value = selectedRow?.[col.name] ?? row[col.name];

                    if (col.type === "status") {
                      const isEmailOtpCol =
                        col.listview === "campaign_management" &&
                        col.name === "email_otp_enabled";
                      const regionEmailOtpEnabled = Boolean(
                        selectedRow?.region_email_otp_enabled ??
                          row.region_email_otp_enabled
                      );
                      const isRegionLocked = isEmailOtpCol && regionEmailOtpEnabled;
                      const checked = isRegionLocked
                        ? true
                        : Boolean(value);
 
                      const switchControl = (
                        <Switch
                          checked={checked}
                          color="primary"
                          readOnly={isRegionLocked}
                          onChange={
                            isRegionLocked
                              ? undefined
                              : e =>
                                  handleStatusToggle(
                                    row,
                                    col.name,
                                    e.target.checked
                                  )
                          }
                          style={
                            isRegionLocked
                              ? { pointerEvents: "none", cursor: "default" }
                              : undefined
                          }
                          inputProps={
                            isRegionLocked
                              ? { readOnly: true, "aria-readonly": true }
                              : undefined
                          }
                        />
                      );

                      return (
                        <TableCell key={index}>
                          {isRegionLocked ? (
                            <Tooltip title="Email OTP is enabled at region level and cannot be changed here">
                              <span style={{ display: "inline-flex" }}>
                                {switchControl}
                              </span>
                            </Tooltip>
                          ) : (
                            switchControl
                          )}
                        </TableCell>
                      );
                    }

                    return <TableCell key={index}>{value?.toString()}</TableCell>;
                  })}

                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {totalData > perPage && (
        <div className={styles.footer}>
          <PerPage limit={perPage} setLimit={(newLimit) => setPagination(prev => ({ ...prev, page: 1, pageSize: newLimit }))} />
          <Pagination
            totalData={totalData}
            limit={perPage}
            offset={offset}
            handlePageClick={offset => {
              handlePaginationChange(offset);
            }} />
        </div>
      )}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{title} Bulk Update</DialogTitle>
        <DialogContent>
          <div>
            {selected.map((s, index) => {
              const rowId = Object.keys(s)[0]
              const row = data.find(e => e[rowId] == s[rowId])
              const display = s.display ?? row?.name ?? rowId;

              return (
                <div key={index}>
                  <strong>{display}</strong>

                  <ul className="mt-2">
                    {"email_otp_enabled" in s && (
                      <li>Email OTP → {s.email_otp_enabled ? "Active" : "Inactive"}</li>
                    )}
                    {"ext_link_enabled" in s && (
                      <li>
                        External Link → {s.ext_link_enabled ? "Active" : "Inactive"}
                      </li>
                    )}
                  </ul>
                </div>
              )
            })}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleBulkUpdate} color="primary">
            Ok
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default BulkTable